import { useState, ReactNode } from "react";
import styled from "styled-components/macro";
import { useFields } from "../../hooks/use-fields";
import { Box, BoxHeader, BoxTitle, BoxContent, BoxNavRight } from "../Box";
import Input from "../Input";
import { ChevronDown, TrashCan } from "../Icons";
import Stack from "../Stack";
import { IconButton } from "../Button";
import usePlan from "../../hooks/use-plan";
import Checkbox from "../Checkbox";
import useLoadAll from "../../hooks/use-load-all";
import { useSkipioApi } from "../../api";
import useLanguage from "../../hooks/use-language";
import { EditableMessageTemplateRule } from ".";
import { OnChange } from "../../hooks/use-optional-state";
import { CampaignResponse } from "../../api/campaigns";

// TODO: Replace this once groups API is typed
type Group = {
  id: string;
  name: string;
};

type Props = {
  value: EditableMessageTemplateRule;
  onChange: OnChange<EditableMessageTemplateRule>;
  onRemove: () => void;
};

function Rule({ value, onChange, onRemove }: Props) {
  const { errors = {} } = value;
  const { field, value: rule } = useFields({ value, onChange });
  const [isExpanded, setIsExpanded] = useState(true);
  const plan = usePlan();

  type Signal = {
    value: EditableMessageTemplateRule["signal"];
    label: string;
  };

  const defaultSignals: Signal[] = [
    { value: "sent_to_provider", label: "this campaign message is sent" },
    { value: "anything", label: "the contact replies, or clicks a Skip Link" },
    { value: "click", label: "the contact clicks on a Skip Link" },
    { value: "expression", label: "the contact replies with an expression" },
  ];

  const sentimentSignals: Signal[] = [
    { value: "positive", label: "the contact replies with positive response" },
    { value: "neutral", label: "the contact replies with neutral response" },
    { value: "negative", label: "the contact replies with negative response" },
  ];

  const signals = [
    ...defaultSignals,
    ...(plan.features.sentiment ? sentimentSignals : []),
  ];

  const skipioApi = useSkipioApi();
  const { data: campaigns = [] }: { data: CampaignResponse[] } = useLoadAll(
    skipioApi.campaigns
  );
  const { data: groups = [] }: { data: Group[] } = useLoadAll(skipioApi.groups);
  const { selectFromLanguage } = useLanguage();

  let explanation = (
    <>
      {rule.signal === "expression" && rule.expression ? (
        <>
          Contact replies "<var data-var="expression">{rule.expression}</var>"
        </>
      ) : (
        signals.find((signal) => signal.value === rule.signal)?.label
      )}
    </>
  );

  const selectedGroup = groups.find(
    (group) => rule.contactGroupId === group.id
  );

  const selectedCampaign = campaigns.find(
    (campaign) => rule.campaignId === campaign.id
  );

  const thens = [
    rule.shouldStopCampaign && <>remove the contact from this campaign</>,
    rule.contactGroupId && (
      <>
        add the contact to{" "}
        {selectedGroup ? <>the "{selectedGroup.name}" group</> : "a group"}
      </>
    ),
    rule.campaignId && (
      <>
        add the contact to{" "}
        {selectedCampaign ? (
          <>the "{selectedCampaign.name}" campaign</>
        ) : (
          "a group"
        )}
      </>
    ),
  ].filter(Boolean);

  const appendExplanation = (added: ReactNode) => {
    explanation = (
      <>
        {explanation}
        {added}
      </>
    );
  };

  if (thens.length) {
    appendExplanation(", Skipio will ");
    if (thens.length === 1) appendExplanation(thens[0]);
    else {
      const beforeAnd = thens.slice(0, -1);
      const and = thens[thens.length - 1];
      for (let i = 0; i < beforeAnd.length; i++) {
        const before = thens[i];
        appendExplanation(before);
        if (i + 1 !== beforeAnd.length) appendExplanation(", ");
      }
      appendExplanation(" and ");
      appendExplanation(and);
    }
    appendExplanation(".");
  } else appendExplanation("...");

  return (
    <Box error={Object.keys(errors).length > 0}>
      <BoxHeader
        style={{
          background: "#fff",
          boxShadow: "none",
        }}
      >
        <BoxTitle style={{ textAlign: "left" }}>Rule</BoxTitle>
        <BoxNavRight style={{ paddingRight: "8px" }}>
          <Stack>
            <IconButton onClick={onRemove}>
              <TrashCan />
            </IconButton>
            <IconButton
              onClick={
                isExpanded
                  ? () => setIsExpanded(false)
                  : () => setIsExpanded(true)
              }
            >
              <ChevronDown
                style={{ transform: `rotate(${isExpanded ? "0" : "-90"}deg)` }}
              />
            </IconButton>
          </Stack>
        </BoxNavRight>
      </BoxHeader>
      {isExpanded && (
        <BoxContent>
          <Stack>
            {selectFromLanguage({
              en: (
                <Explanation data-ignore="true">When {explanation}</Explanation>
              ),
              default: null,
            })}

            <Input
              label="Execute Rule When"
              type="select"
              {...field("signal")}
              error={errors.signal}
            >
              {signals.map((signal) => (
                <option value={signal.value} key={signal.value}>
                  {signal.label}
                </option>
              ))}
            </Input>
            {value.signal === "expression" && (
              <Input
                label="Contact reply contains this expression"
                error={errors.expression}
                {...field("expression")}
              />
            )}
            <Checkbox {...field("shouldStopCampaign")}>
              Remove the contact from this campaign?
            </Checkbox>
            <Input
              label="Add the contact to another group"
              type="select"
              {...field("contactGroupId")}
              error={errors.contactGroupId}
            >
              <option></option>
              {groups.map((group) => (
                <option value={group.id} key={group.id}>
                  {group.name}
                </option>
              ))}
            </Input>
            <Input
              label="Add the contact to another campaign"
              type="select"
              {...field("campaignId")}
              error={errors.campaignId}
            >
              <option></option>
              {campaigns.map((campaign) => (
                <option value={campaign.id} key={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </Input>
          </Stack>
        </BoxContent>
      )}
    </Box>
  );
}

const Explanation = styled.div`
  line-height: 1.6em;
  color: #676767;
  font-style: italic;
`;

export default Rule;
